import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LandingPage.css';
import Carousel from './Carousel';
import { Helmet } from 'react-helmet';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { verificarDisponibilidadHabitacion } from '../data/reservas';
import GuestReviews from './GuestReviews';
import RecomendacionesDestacadas from './RecomendacionesDestacadas';
import { formatearPrecio } from '../utils/dateUtils';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import es from 'date-fns/locale/es';
import Modal from './Modal';

// Registrar el locale español
registerLocale('es', es);

// Componente de input personalizado para DatePicker
const CustomInput = forwardRef(({ value, onClick, disabled }, ref) => (
  <button 
    type="button"
    className="fecha-input" 
    onClick={onClick} 
    ref={ref}
    disabled={disabled}
  >
    {value}
  </button>
));

const LandingPage = () => {
  const [habitaciones, setHabitaciones] = useState([]);
  const [roomAvailability, setRoomAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentRoomSlide, setCurrentRoomSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const roomCarouselRef = useRef(null);
  const navigate = useNavigate();
  
  // Estados para el modal de reserva rápida
  const [modalReservaAbierto, setModalReservaAbierto] = useState(false);
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);
  const [fechaEntrada, setFechaEntrada] = useState(null);
  const [fechaSalida, setFechaSalida] = useState(null);
  const [cantidadPersonas, setCantidadPersonas] = useState(1);
  const [errorModal, setErrorModal] = useState('');
  const [verificandoDisponibilidad, setVerificandoDisponibilidad] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Estado para controlar la visibilidad de los controles del carousel cuando el calendario está abierto
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  const images = [
    '/images/accommodation1.jpg',
    '/images/accommodation2.jpg',
    '/images/accommodation3.jpg',
    '/images/accommodation4.jpg',
    '/images/accommodation5.jpg'
  ];

  // Función para cargar las habitaciones desde Firestore
  useEffect(() => {
    const fetchHabitaciones = async () => {
      try {
        setLoading(true);
        const habitacionesCollection = collection(db, 'habitaciones');
        const habitacionesSnapshot = await getDocs(habitacionesCollection);
        
        if (habitacionesSnapshot.empty) {
          console.log('No se encontraron habitaciones en Firestore');
          setHabitaciones([]);
        } else {
          const habitacionesList = habitacionesSnapshot.docs.map(doc => {
            const data = doc.data();
            // Asegurar que haya una lista de imágenes disponible
            let imagenes = data.imagenes || [];
            
            // Si no hay imágenes, usar la imagen principal si existe
            if (imagenes.length === 0 && data.imagen) {
              imagenes = [data.imagen];
            }
            
            // Si todavía no hay imágenes, usar un placeholder
            if (imagenes.length === 0) {
              imagenes = ['/images/room-placeholder.jpg'];
            }
            
            return {
            id: doc.id,
              ...data,
              imagen: imagenes[0], // Primera imagen como imagen principal
              imagenes: imagenes   // Todas las imágenes para el carousel
            };
          });
          
          setHabitaciones(habitacionesList);
          console.log('Habitaciones cargadas desde Firestore:', habitacionesList.length);
        }
      } catch (error) {
        console.error('Error al cargar habitaciones:', error);
        setError('No se pudieron cargar las habitaciones. Por favor, intente más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchHabitaciones();
  }, []);

  // Verificar disponibilidad de habitaciones
  useEffect(() => {
    const checkAvailability = async () => {
      if (habitaciones.length === 0) return;
      
      try {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const availabilityPromises = habitaciones.map(async (habitacion) => {
          const disponible = await verificarDisponibilidadHabitacion(
            habitacion.id,
            today.toISOString().split('T')[0],
            tomorrow.toISOString().split('T')[0]
          );
          return { id: habitacion.id, disponible };
        });

        const results = await Promise.all(availabilityPromises);
        const availabilityMap = results.reduce((acc, { id, disponible }) => {
          acc[id] = disponible;
          return acc;
        }, {});

        setRoomAvailability(availabilityMap);
      } catch (error) {
        console.error('Error al verificar disponibilidad:', error);
      }
    };

    checkAvailability();
  }, [habitaciones]);

  // Detectar si la pantalla es móvil
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Comprobar al inicio
    checkIfMobile();
    
    // Comprobar al cambiar el tamaño de la ventana
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Función para ir a la siguiente habitación
  const nextRoom = () => {
    if (!habitaciones.length) return;
    
    setCurrentRoomSlide((prev) => 
      prev === habitaciones.length - 1 ? 0 : prev + 1
    );
  };

  // Función para ir a la habitación anterior
  const prevRoom = () => {
    if (!habitaciones.length) return;
    
    setCurrentRoomSlide((prev) => 
      prev === 0 ? habitaciones.length - 1 : prev - 1
    );
  };

  // Función para abrir el modal de reserva rápida
  const abrirModalReserva = (habitacion) => {
    setHabitacionSeleccionada(habitacion);
    // Inicializar fechas: hoy y mañana
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setFechaEntrada(today);
    setFechaSalida(tomorrow);
    // Inicializar con 1 persona o la última selección
    setCantidadPersonas(1);
    setErrorModal('');
    setCurrentImageIndex(0); // Reiniciar índice de imagen al abrir el modal
    setModalReservaAbierto(true);
  };

  // Función para cerrar el modal
  const cerrarModalReserva = () => {
    setModalReservaAbierto(false);
    setHabitacionSeleccionada(null);
  };
  
  // Función para manejar cambios en la fecha de entrada
  const handleFechaEntradaChange = (date) => {
    setFechaEntrada(date);
    setErrorModal('');
    
    // Si la fecha de entrada es posterior o igual a la fecha de salida
    // ajustar la fecha de salida para que sea el día siguiente
    if (fechaSalida && date >= fechaSalida) {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      setFechaSalida(nextDay);
    }
  };
  
  // Función para manejar cambios en la fecha de salida
  const handleFechaSalidaChange = (date) => {
    setFechaSalida(date);
    setErrorModal('');
  };
  
  // Función para validar y navegar al proceso de reserva
  const continuarReserva = async () => {
    if (!fechaEntrada || !fechaSalida) {
      setErrorModal('Por favor, selecciona fechas de entrada y salida.');
      return;
    }
    
    if (fechaEntrada >= fechaSalida) {
      setErrorModal('La fecha de salida debe ser posterior a la fecha de entrada.');
      return;
    }
    
    if (cantidadPersonas <= 0 || cantidadPersonas > (habitacionSeleccionada.capacity || 1)) {
      setErrorModal(`Por favor, selecciona entre 1 y ${habitacionSeleccionada.capacity || 1} personas.`);
      return;
    }
    
    // Formatear fechas para la verificación de disponibilidad (yyyy-mm-dd)
    const fechaEntradaStr = fechaEntrada.toISOString().split('T')[0];
    const fechaSalidaStr = fechaSalida.toISOString().split('T')[0];
    
    try {
      setVerificandoDisponibilidad(true);
      setErrorModal('');
      
      // Verificar disponibilidad antes de navegar
      const disponible = await verificarDisponibilidadHabitacion(
        habitacionSeleccionada.id,
        fechaEntradaStr,
        fechaSalidaStr
      );
      
      if (!disponible) {
        setErrorModal(`Lo sentimos, la habitación no está disponible para las fechas seleccionadas. Por favor, selecciona otras fechas.`);
        setVerificandoDisponibilidad(false);
        return;
      }
      
      // Si está disponible, navegar a la página de reserva
      navigate(`/reservar?checkin=${fechaEntradaStr}&checkout=${fechaSalidaStr}&persons=${cantidadPersonas}&room=${habitacionSeleccionada.id}`);
    } catch (error) {
      console.error('Error al verificar disponibilidad:', error);
      setErrorModal('Ocurrió un error al verificar la disponibilidad. Por favor, inténtalo de nuevo.');
      setVerificandoDisponibilidad(false);
    }
  };

  // Funciones para el carousel de imágenes en el modal
  const nextImage = () => {
    if (!habitacionSeleccionada || !habitacionSeleccionada.imagenes) return;
    setCurrentImageIndex((prev) => 
      prev === habitacionSeleccionada.imagenes.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!habitacionSeleccionada || !habitacionSeleccionada.imagenes) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? habitacionSeleccionada.imagenes.length - 1 : prev - 1
    );
  };

  return (
    <main className="landing-page">
      <Helmet>
        <title>Hostal en Futaleufú | Hospedaje Cómodo y Económico | Reservas Online - Hosteria Futaleufu</title>
        <meta name="description" content="Descubre el mejor hostal en Futaleufú. Hospedaje cómodo, económico y con excelente ubicación. Reserva online y disfruta de la mejor experiencia en la Patagonia chilena." />
        <meta name="keywords" content="hostal futaleufú, hospedaje futaleufú, alojamiento futaleufú, hostal económico futaleufú, hotel futaleufú, cabañas futaleufú" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Hostal en Futaleufú | Hospedaje Cómodo y Económico" />
        <meta property="og:description" content="Descubre el mejor hostal en Futaleufú. Hospedaje cómodo, económico y con excelente ubicación." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tuhostalfutaleufu.com" />
        <meta property="og:image" content="/images/accommodation1.jpg" />
      </Helmet>

      {/* Hero Section with Carousel */}
      <section className="hero-section" aria-label="Bienvenida">
        <Carousel imagenes={images} altura="400px" className="hero-carousel" showWelcomeText={true} />
      </section>

      {/* Availability Section */}
      <section className="availability-section" aria-label="Disponibilidad">
        <div className="availability-content">
          <h2>Si desea revisar disponibilidad y hacer una reserva con nosotros, haga click en el siguiente botón:</h2>
          <Link to="/reservar" className="availability-button">
            Revisar disponibilidad
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" aria-label="Sobre nosotros">
        <div className="about-content">
          <div className="about-text">
            <h2>Nuestro Hospedaje</h2>
            <p>
              Bienvenidos a nuestro acogedor hospedaje.
            </p>
            <p>
              Nuestras habitaciones están diseñadas pensando en ti, con todas las 
              comodidades necesarias para hacer de tu estadía una experiencia cómoda y agradable (toallas, 
              productos de higiene personal, calefacción, etc).
            </p>
            <p>
              Además, contamos con una cocina común para que puedas preparar tus alimentos, un tecito caliente antes de ir a dormir
              y un cafecito al despertar (por nuestra cuenta).
            </p>
          </div>
          <div className="about-image">
            <img src="/images/about-image.jpg" alt="Hostal en Futaleufú - Recepción y áreas comunes" />
            <h3 className="image-caption">Barto, el recepcionista los espera</h3>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Rooms Section */}
      <section className="rooms-section" aria-label="Nuestras habitaciones">
        <h2>Nuestras Habitaciones</h2>
        
        {loading ? (
          <div className="loading-message">Cargando habitaciones...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : habitaciones.length === 0 ? (
          <div className="no-rooms-message">No hay habitaciones disponibles en este momento.</div>
        ) : isMobile ? (
          // Versión móvil - Carrusel
          <div className="rooms-carousel" ref={roomCarouselRef}>
            <button 
              className="carousel-btn prev-btn" 
              onClick={prevRoom}
              aria-label="Habitación anterior"
              type="button"
            >
              &lsaquo;
            </button>
            
            <div className="carousel-container">
              {habitaciones.map((habitacion, index) => (
                <div 
                  key={habitacion.id} 
                  className={`carousel-item ${index === currentRoomSlide ? 'active' : ''}`}
                >
                  <div className="room-card">
                    <div className="room-image">
                      <img src={habitacion.imagen} alt={habitacion.nombre} />
                      <div className={`availability-indicator ${roomAvailability[habitacion.id] ? 'available' : 'unavailable'}`}>
                        Hoy
                      </div>
                    </div>
                    <div className="room-information">
                      <h3>{habitacion.nombre}</h3>
                      <div className="room-prices">
                        {habitacion.preciosPorOcupacion && Object.entries(habitacion.preciosPorOcupacion).map(([personas, precio]) => (
                          <p key={personas} className="room-price">
                            <span className="price-persons-icons">
                              {Array.from({ length: Number(personas) }, (_, i) => (
                                <svg key={i} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              ))}
                            </span>
                            <span className="price-text">{personas} {personas === '1' ? 'persona' : 'personas'}: </span>
                            <span className="price-value">{formatearPrecio(precio)} por noche</span>
                          </p>
                        ))}
                      </div>
                      <p className="room-description">{habitacion.descripcion}</p>
                      <button onClick={() => abrirModalReserva(habitacion)} className="reserve-button">
                        Reservar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              className="carousel-btn next-btn" 
              onClick={nextRoom}
              aria-label="Siguiente habitación"
              type="button"
            >
              &rsaquo;
            </button>
            
            <div className="carousel-dots">
              {habitaciones.map((_, index) => (
                <span 
                  key={index} 
                  className={`dot ${index === currentRoomSlide ? 'active' : ''}`}
                  onClick={() => setCurrentRoomSlide(index)}
                ></span>
              ))}
            </div>
          </div>
        ) : (
          // Versión escritorio - Grid
          <div className="rooms-grid">
            {habitaciones.map((habitacion) => (
              <div key={habitacion.id} className="room-card">
                <div className="room-image">
                  <img src={habitacion.imagen} alt={habitacion.nombre} />
                  <div className={`availability-indicator ${roomAvailability[habitacion.id] ? 'available' : 'unavailable'}`}>
                    Hoy
                  </div>
                </div>
                <div className="room-information">
                  <h3>{habitacion.nombre}</h3>
                  <div className="room-prices">
                    {habitacion.preciosPorOcupacion && Object.entries(habitacion.preciosPorOcupacion).map(([personas, precio]) => (
                      <p key={personas} className="room-price">
                        <span className="price-persons-icons">
                          {Array.from({ length: Number(personas) }, (_, i) => (
                            <svg key={i} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          ))}
                        </span>
                        <span className="price-text">{personas} {personas === '1' ? 'persona' : 'personas'}: </span>
                        <span className="price-value">{formatearPrecio(precio)} por noche</span>
                      </p>
                    ))}
                  </div>
                  <p className="room-description">{habitacion.descripcion}</p>
                  <button onClick={() => abrirModalReserva(habitacion)} className="reserve-button">
                    Reservar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal de Reserva Rápida */}
      {modalReservaAbierto && habitacionSeleccionada && (
        <div className="modal-overlay">
          <div className="modal-reserva-rapida">
            <button className="cerrar-modal" onClick={cerrarModalReserva}>×</button>
            <h3>Reservar {habitacionSeleccionada.nombre}</h3>
            
            <div className="modal-reserva-contenido">
              <div className="modal-reserva-imagenes">
                {habitacionSeleccionada.imagenes && habitacionSeleccionada.imagenes.length > 0 ? (
                  <div className={`modal-carousel ${datePickerOpen ? 'date-picker-open' : ''}`}>
                    <button 
                      className="modal-carousel-btn prev-btn" 
                      onClick={prevImage}
                      aria-label="Imagen anterior"
                      type="button"
                    >
                      &lsaquo;
                    </button>
                    
                    <div className="modal-carousel-container">
                      {habitacionSeleccionada.imagenes.map((imagen, index) => (
                        <div 
                          key={index} 
                          className={`modal-carousel-item ${index === currentImageIndex ? 'active' : ''}`}
                        >
                          <img src={imagen} alt={`${habitacionSeleccionada.nombre} - Imagen ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                    
                    <button 
                      className="modal-carousel-btn next-btn" 
                      onClick={nextImage}
                      aria-label="Siguiente imagen"
                      type="button"
                    >
                      &rsaquo;
                    </button>
                    
                    {habitacionSeleccionada.imagenes.length > 1 && (
                      <div className="modal-carousel-dots">
                        {habitacionSeleccionada.imagenes.map((_, index) => (
                          <span 
                            key={index} 
                            className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(index)}
                          ></span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <img 
                    src={habitacionSeleccionada.imagen || '/images/room-placeholder.jpg'} 
                    alt={habitacionSeleccionada.nombre} 
                    className="modal-reserva-imagen-fallback"
                  />
                )}
              </div>
              
              <div className="modal-reserva-detalles">
                <p className="modal-reserva-descripcion">{habitacionSeleccionada.descripcion}</p>
                <p className="modal-reserva-capacidad">Capacidad: {habitacionSeleccionada.capacity || 1} personas</p>
                
                <div className="modal-reserva-precios">
                  <h4>Seleccionar personas:</h4>
                  <ul>
                    {habitacionSeleccionada.preciosPorOcupacion && 
                      Object.entries(habitacionSeleccionada.preciosPorOcupacion).map(([personas, precio]) => (
                        <li 
                          key={personas} 
                          className={cantidadPersonas === Number(personas) ? 'precio-destacado' : ''}
                          onClick={() => {
                            setCantidadPersonas(Number(personas));
                            setErrorModal('');
                          }}
                        >
                          <div className="precio-item-left">
                            <span className="personas-icons">
                              {Array.from({ length: Number(personas) }, (_, i) => (
                                <svg key={i} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              ))}
                            </span>
                            <span>{personas} {personas === '1' ? 'persona' : 'personas'}</span>
                          </div>
                          <span className="precio-value">{formatearPrecio(precio)}</span>
                        </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="modal-reserva-form">
              <div className="modal-reserva-fechas">
                <div className="modal-reserva-fecha">
                  <label>Fecha de entrada:</label>
                  <DatePicker
                    selected={fechaEntrada}
                    onChange={handleFechaEntradaChange}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    disabled={verificandoDisponibilidad}
                    customInput={<CustomInput />}
                    onCalendarOpen={() => setDatePickerOpen(true)}
                    onCalendarClose={() => setDatePickerOpen(false)}
                  />
                </div>
                
                <div className="modal-reserva-fecha">
                  <label>Fecha de salida:</label>
                  <DatePicker
                    selected={fechaSalida}
                    onChange={handleFechaSalidaChange}
                    minDate={new Date(fechaEntrada?.getTime() + 86400000)}
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    disabled={verificandoDisponibilidad}
                    customInput={<CustomInput />}
                    onCalendarOpen={() => setDatePickerOpen(true)}
                    onCalendarClose={() => setDatePickerOpen(false)}
                  />
                </div>
              </div>
              
              {errorModal && <p className="modal-reserva-error">{errorModal}</p>}
              
              <div className="modal-reserva-botones">
                <button 
                  type="button" 
                  className="btn-cancelar" 
                  onClick={cerrarModalReserva}
                  disabled={verificandoDisponibilidad}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn-continuar" 
                  onClick={continuarReserva}
                  disabled={verificandoDisponibilidad}
                >
                  {verificandoDisponibilidad ? 'Verificando disponibilidad...' : 'Continuar Reserva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Section */}
      <section className="location-section" aria-label="Ubicación">
        <div className="location-content">
          <h2>Ubicación Privilegiada</h2>
          <div className="location-details">
            <div className="location-text">
              <p>
                Nuestro hospedaje se encuentra en una ubicación estratégica, cerca de los principales 
                puntos de interés y con fácil acceso a las principales rutas.
              </p>
              <ul className="location-features">
                <li>✓ A una cuadra de la plaza</li>
                <li>✓ Fácil acceso</li>
                <li>✓ Entorno tranquilo</li>
                <li>✓ Buen punto de partida para trekkings</li>
              </ul>
            </div>
            <div className="location-map">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d181.82362143779284!2d-71.8690288066864!3d-43.18477700960167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x961ddea2cd68e425%3A0x939438e5f8d83981!2zSG9zdGVyw61hIEZ1dGFsZXVmw7o!5e0!3m2!1ses!2scl!4v1744069464355!5m2!1ses!2scl" 
                width="300" 
                height="300" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación del Hostal en Futaleufú"
              >
              </iframe>
              <div className="map-placeholder">
                Mapa de ubicación
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GuestReviews />

      {/* Recomendaciones Section */}
      <section className="recomendaciones-section" aria-label="Actividades">
        <h2>Actividades</h2>
        <p>Descubre experiencias inolvidables en Futaleufú y sus alrededores</p>
        <div className="recomendaciones-content">
          <RecomendacionesDestacadas maxItems={4} />
          <Link to="/recomendaciones" className="ver-todas-button">
            Ver todas las actividades
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer" role="contentinfo">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Contacto</h3>
            <p>Email: hosteriafutaleufu@gmail.com</p>
            <p>Teléfono: +56950620081</p>
          </div>
          <div className="footer-section">
            <h3>Dirección</h3>
            <p>Pedro Aguirre Cerda 393</p>
            <p>Futaleufú, Los Lagos</p>
          </div>
          <div className="footer-section">
            <h3>Síguenos</h3>
            <div className="social-links">
              <a href="#" target="_blank" rel="noopener noreferrer">Instagram</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href="https://api.whatsapp.com/send/?phone=56950620081&text&type=phone_number&app_absent=0" 
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contáctenos por WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" className="whatsapp-icon">
          <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </main>
  );
};

export default LandingPage; 